/*
=========================================================
* Marketing Site - Brand Constants
=========================================================
*
* Derives brand-specific strings (domain, labels, copy) from the build-time
* REACT_APP_BRAND constant. Every caller imports from here so brand-specific
* text is never duplicated or drifts between pages.
*
* Strict at module load: unknown REACT_APP_BRAND throws during build.
*/

import { APP_BRAND, APP_BRAND_KEY } from "../version";
import { getDisplayTrialDays } from "./promo";

let marketingHost;
let appHost;
let blogTitle;
let blogHeading;
let blogMetaTitle;
let blogMetaDescription;
let blogHeroSubtitle;
let blogCtaHeading;
let blogCtaBody;
let twitterHandle;
let ctaLearnMoreText;
// Sister brand (cross-link shown at the bottom of every blog post + in
// the footer). Descriptive anchor text is the SEO value.
let sisterSiteUrl;
let sisterSiteCta;
let sisterSiteName;
// Optional H2 sectioning for the /blog index page — buckets articles under
// keyword-targeted H2s instead of one undifferentiated grid. null disables it
// (single-grid layout used). Each section maps a list of post categories to a
// keyword-rich H2 heading. Categories not listed fall into the trailing
// "More articles" bucket.
let blogSeoSections;
// Affiliate terms (/affiliates/terms): the brand's subscription plans and the
// plan used in the commission example. Prices must match the backend's
// ai-voice-typescript/src/config/pricing.config.ts for the brand.
let affiliatePlans;
let affiliateExamplePlan;

switch (APP_BRAND_KEY) {
  case "ringfoods":
    marketingHost = "www.ringfoods.com";
    appHost = "app.ringfoods.com";
    blogTitle = "The RingFoods Blog";
    blogHeading = "The RingFoods Blog";
    blogMetaTitle =
      "Restaurant Technology Blog | RingFoods — Tips, Guides & Industry Insights";
    blogMetaDescription =
      "Practical guides and insights for restaurant owners on reducing missed calls, managing phone stress, cutting costs, and using AI to improve operations.";
    blogHeroSubtitle =
      "Insights on restaurant technology, AI phone automation, and building a team that doesn't dread the phone.";
    blogCtaHeading = "Ready to Eliminate Phone Stress?";
    blogCtaBody =
      `Start your ${getDisplayTrialDays()}-day free trial. You won't be charged until your trial ends.`;
    twitterHandle = "@ringfoods";
    affiliatePlans = [
      { name: "Basic", monthlyPrice: 100 },
      { name: "Professional", monthlyPrice: 200 },
      { name: "Enterprise", monthlyPrice: 300 },
    ];
    affiliateExamplePlan = affiliatePlans[1];
    ctaLearnMoreText = "Learn how RingFoods works for restaurants";
    sisterSiteUrl = "https://www.ringoperator.com";
    sisterSiteName = "RingOperator";
    sisterSiteCta = "Running a non-restaurant business? See RingOperator for service businesses";
    blogSeoSections = [
      {
        heading: "Restaurant Phone Stress & Missed Call Recovery",
        categories: ["Tips", "Operations", "Industry"],
      },
      {
        heading: "Reservation Management & No-Show Reduction",
        categories: ["Business"],
      },
      {
        heading: "AI Phone Systems vs Traditional Answering Services",
        categories: ["Guide", "Comparison"],
      },
    ];
    break;
  case "ringoperator":
    marketingHost = "www.ringoperator.com";
    appHost = "app.ringoperator.com";
    blogTitle = "The RingOperator Blog";
    blogHeading = "The RingOperator Blog";
    blogMetaTitle =
      "Business Technology Blog | RingOperator — AI Voice Agent Tips & Guides";
    blogMetaDescription =
      "Practical guides for service businesses on AI call handling, lead capture, appointment booking, and reducing missed calls with a voice agent.";
    blogHeroSubtitle =
      "Insights on AI voice agents, lead capture, appointment scheduling, and freeing your team from the phone.";
    blogCtaHeading = "Ready to Stop Missing Leads?";
    blogCtaBody =
      `Start your ${getDisplayTrialDays()}-day free trial. You won't be charged until your trial ends.`;
    twitterHandle = "@ringoperator";
    affiliatePlans = [
      { name: "Starter", monthlyPrice: 25 },
      { name: "Growth", monthlyPrice: 100 },
      { name: "Scale", monthlyPrice: 300 },
    ];
    affiliateExamplePlan = affiliatePlans[1];
    ctaLearnMoreText = "Learn how RingOperator works for your business";
    sisterSiteUrl = "https://www.ringfoods.com";
    sisterSiteName = "RingFoods";
    sisterSiteCta = "Run a restaurant? See RingFoods for restaurant-specific features";
    blogSeoSections = null;
    break;
  default:
    throw new Error(
      `constants/brand.js: unknown APP_BRAND_KEY '${APP_BRAND_KEY}'.`
    );
}

export const BRAND_KEY = APP_BRAND_KEY; // 'ringfoods' | 'ringoperator'
export const BRAND_NAME = APP_BRAND; // 'RingFoods' | 'RingOperator'
export const MARKETING_HOST = marketingHost;
export const MARKETING_URL = `https://${marketingHost}`;
export const APP_HOST = appHost;
export const BLOG_TITLE = blogTitle;
export const BLOG_HEADING = blogHeading;
export const BLOG_META_TITLE = blogMetaTitle;
export const BLOG_META_DESCRIPTION = blogMetaDescription;
export const BLOG_HERO_SUBTITLE = blogHeroSubtitle;
export const BLOG_CTA_HEADING = blogCtaHeading;
export const BLOG_CTA_BODY = blogCtaBody;
export const TWITTER_HANDLE = twitterHandle;
export const CTA_LEARN_MORE_TEXT = ctaLearnMoreText;
export const SISTER_SITE_URL = sisterSiteUrl;
export const SISTER_SITE_NAME = sisterSiteName;
export const SISTER_SITE_CTA = sisterSiteCta;
export const BLOG_SEO_SECTIONS = blogSeoSections;
export const AFFILIATE_PLANS = affiliatePlans;
export const AFFILIATE_EXAMPLE_PLAN = affiliateExamplePlan;
